import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/notificationSlice';
import { fetchUserLicenses, fetchProducts } from '../../store/slices/licenseSlice';
import { AppDispatch } from '../../store/store';
import Modal from '../ui/Modal';
import { SubscriptionService } from '../../api/generated/services/SubscriptionService';
import { ApiError } from '../../api/generated/core/ApiError';
import { helper } from '../../utils/helper';

interface LicenseCancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  licenseId: string;
  licenseName: string;
  licenseType?: string;
  /** Subscription expiration date (`SubscriptionView.expired`). */
  expirationDate?: string | null;
  /**
   * If the subscription has a pending scheduled downgrade, the cancel call
   * will silently drop it on the backend - surface that explicitly to the user.
   */
  hasPendingScheduledChange?: boolean;
}

const LicenseCancelModal: React.FC<LicenseCancelModalProps> = ({
  isOpen,
  onClose,
  licenseId,
  licenseName,
  licenseType,
  expirationDate,
  hasPendingScheduledChange = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  const formattedExpiration = expirationDate
    ? new Date(expirationDate).toLocaleDateString()
    : '-';

  const handleCancel = async () => {
    setIsLoading(true);
    try {
      await SubscriptionService.postAppSubscriptionCancel(licenseId);

      dispatch(addNotification({
        type: 'success',
        title: t('notification_success'),
        message: t('license_cancel_success', { licenseName }),
        autoHideDuration: 5000,
      }));

      dispatch(fetchUserLicenses({ page: 1, pageSize: 100, silent: true }));
      if (licenseType) {
        dispatch(fetchProducts(licenseType));
      }

      onClose();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);

      let errorMessage = t('license_cancel_error', { licenseName });

      if (error instanceof ApiError) {
        const parsedBody = helper.parseApiErrorBody(error);
        if (parsedBody && parsedBody.error) {
          errorMessage = parsedBody.error;
        }
      }

      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: errorMessage,
        autoHideDuration: 8000,
      }));

      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('license_cancel_modal_title')}
      hideCloseButton={true}
      isCentered={true}
    >
      <div className="modal-success">
        <div className="modal-success-text">
          {t('license_cancel_modal_text', { date: formattedExpiration })}
        </div>
        {hasPendingScheduledChange && (
          <div className="modal-success-text">
            {t('license_cancel_modal_text_with_pending')}
          </div>
        )}

        <div className="modal-buttons">
          <button
            className="modal-button confirm"
            onClick={onClose}
            disabled={isLoading}
          >
            {t('license_cancel_modal_back')}
          </button>
          <button
            className="modal-button cancel"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {isLoading ? t('license_cancel_processing') : t('license_cancel_modal_confirm_v2')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LicenseCancelModal;
